import logger from '@/utils/logger';
/**
 * Sistema de caché de audio usando IndexedDB
 * Permite descargar y almacenar audios de peticiones para uso offline
 */

const DB_NAME = 'AudioCacheDB';
const DB_VERSION = 1;
const STORE_NAME = 'audioFiles';


/**
 * Detecta el MIME real de un ArrayBuffer de audio a partir de sus primeros bytes.
 * Añadido feb-2026 (bug iPad Air 5 en producción): algunas versiones de
 * Safari iOS rechazan un blob con MIME incorrecto lanzando NotSupportedError /
 * MEDIA_ERR_SRC_NOT_SUPPORTED. Backends/CDN a veces cambian el Content-Type
 * (p.ej. `application/octet-stream`). Detectar el tipo real desde magic bytes
 * hace el pipeline robusto frente a proxies o cabeceras alteradas.
 *
 * Devuelve 'audio/mpeg', 'audio/mp4' (m4a) o null si no se reconoce.
 */
export const _detectAudioMime = (arrayBuffer) => {
  try {
    if (!arrayBuffer || arrayBuffer.byteLength < 12) return null;
    const bytes = new Uint8Array(arrayBuffer, 0, 12);
    // ID3v2 tag (MP3 con tags) → "ID3"
    if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
      return 'audio/mpeg';
    }
    // MPEG frame sync (MP3 sin tags) → 0xFF Ex (0xFF 0xFB/0xFA/0xF3/0xF2/0xE3/0xE2)
    if (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0) {
      return 'audio/mpeg';
    }
    // MP4/M4A → bytes 4-7 == "ftyp"
    if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
      return 'audio/mp4';
    }
    // OggS → "OggS"
    if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
      return 'audio/ogg';
    }
    // WAV / RIFF → "RIFF"..."WAVE"
    if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
      return 'audio/wav';
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Abre la base de datos IndexedDB
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Crear el store si no existe
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'audioFileId' });
        objectStore.createIndex('eventId', 'eventId', { unique: false });
        objectStore.createIndex('downloadedAt', 'downloadedAt', { unique: false });
        logger.log('✅ ObjectStore de audio creado');
      }
    };
  });
};

/**
 * Guarda un audio en la caché
 */
export const saveAudioToCache = async (audioFileId, audioBlob, eventId, metadata = {}) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const data = {
      audioFileId,
      eventId,
      blob: audioBlob,
      size: audioBlob.size,
      type: audioBlob.type,
      downloadedAt: new Date().toISOString(),
      metadata: {
        title: metadata.title || '',
        artist: metadata.artist || '',
        ...metadata
      }
    };
    
    await new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    logger.log(`✅ Audio guardado en caché: ${audioFileId} (${Math.round(audioBlob.size / 1024)}KB)`);
    return true;
  } catch (error) {
    console.error('❌ Error guardando audio en caché:', error);
    return false;
  }
};

/**
 * Obtiene un audio de la caché
 */
export const getAudioFromCache = async (audioFileId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const data = await new Promise((resolve, reject) => {
      const request = store.get(audioFileId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (data && data.blob) {
      logger.log(`✅ Audio recuperado de caché: ${audioFileId}`);
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error obteniendo audio de caché:', error);
    return null;
  }
};

/**
 * Verifica si un audio está en caché
 */
export const isAudioCached = async (audioFileId) => {
  try {
    const data = await getAudioFromCache(audioFileId);
    return !!data;
  } catch (error) {
    return false;
  }
};

/**
 * Obtiene todos los audios de un evento
 */
export const getEventAudios = async (eventId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('eventId');
    
    const audios = await new Promise((resolve, reject) => {
      const request = index.getAll(eventId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return audios || [];
  } catch (error) {
    console.error('❌ Error obteniendo audios del evento:', error);
    return [];
  }
};

/**
 * Elimina un audio de la caché
 */
export const deleteAudioFromCache = async (audioFileId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(audioFileId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    logger.log(`🗑️ Audio eliminado de caché: ${audioFileId}`);
    return true;
  } catch (error) {
    console.error('❌ Error eliminando audio de caché:', error);
    return false;
  }
};

/**
 * Elimina todos los audios de un evento
 */
export const deleteEventAudios = async (eventId) => {
  try {
    const audios = await getEventAudios(eventId);
    
    for (const audio of audios) {
      await deleteAudioFromCache(audio.audioFileId);
    }
    
    logger.log(`🗑️ ${audios.length} audios eliminados del evento ${eventId}`);
    return true;
  } catch (error) {
    console.error('❌ Error eliminando audios del evento:', error);
    return false;
  }
};

/**
 * Obtiene estadísticas de la caché
 */
export const getCacheStats = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const allAudios = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    const totalSize = allAudios.reduce((sum, audio) => sum + (audio.size || 0), 0);
    const totalCount = allAudios.length;
    
    // Agrupar por evento
    const byEvent = allAudios.reduce((acc, audio) => {
      if (!acc[audio.eventId]) {
        acc[audio.eventId] = { count: 0, size: 0 };
      }
      acc[audio.eventId].count++;
      acc[audio.eventId].size += audio.size || 0;
      return acc;
    }, {});
    
    return {
      totalCount,
      totalSize,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
      byEvent,
      audios: allAudios
    };
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas:', error);
    return { totalCount: 0, totalSize: 0, totalSizeMB: '0', byEvent: {}, audios: [] };
  }
};

/**
 * Limpia toda la caché
 */
export const clearAllCache = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    await new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    logger.log('🗑️ Caché de audio limpiada completamente');
    return true;
  } catch (error) {
    console.error('❌ Error limpiando caché:', error);
    return false;
  }
};

/**
 * Descarga y cachea todos los audios de un evento
 */
export const downloadEventAudios = async (eventId, peticiones, API, token, onProgress) => {
  const audiosToDownload = peticiones.filter(p => p.audio_file_id);
  const total = audiosToDownload.length;
  let completed = 0;
  let errors = 0;
  
  logger.log(`📥 Descargando ${total} audios para el evento ${eventId}...`);
  
  for (const peticion of audiosToDownload) {
    try {
      // Verificar si ya está en caché
      const cached = await isAudioCached(peticion.audio_file_id);
      if (cached) {
        logger.log(`⏭️ Audio ya en caché: ${peticion.audio_file_id}`);
        completed++;
        if (onProgress) onProgress({ completed, total, current: peticion, errors });
        continue;
      }
      
      // Descargar con manejo de reintentos
      logger.log(`📥 Descargando: ${peticion.title || peticion.titulo} - ${peticion.artist || peticion.artista}`);
      
      const downloadWithRetry = async (attempt = 1) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        try {
          const response = await fetch(`${API}/audio/${peticion.audio_file_id}`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Accept': 'audio/mpeg, audio/mp4, audio/*;q=0.9, */*;q=0.5',
              'Cache-Control': 'no-cache'
            },
            signal: controller.signal,
            cache: 'no-store'
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            // RCA feb-2026: distinguir status (403/401/404) para no mostrar
            // "formato no soportado" cuando en realidad es un fallo de permisos.
            let msg = `HTTP ${response.status}`;
            if (response.status === 403) msg = 'sin permiso (la cuenta actual no es el DJ ni el asistente del evento)';
            else if (response.status === 401) msg = 'sesión caducada';
            else if (response.status === 404) msg = 'el archivo ya no existe en el servidor';
            const err = new Error(msg);
            err.httpStatus = response.status;
            throw err;
          }
          
          // Usar arrayBuffer para mayor compatibilidad
          const arrayBuffer = await response.arrayBuffer();
          // feb-2026 (bug iPad Air 5): validar que el buffer tenga tamaño mínimo
          // razonable (>1KB) y una firma de audio válida. Si el backend/edge
          // devolvió HTML de error o bytes corruptos, la creación del Blob con
          // MIME audio/mpeg fallaría al reproducir con MEDIA_ERR_SRC_NOT_SUPPORTED.
          if (arrayBuffer.byteLength < 1024) {
            throw new Error(`respuesta demasiado pequeña (${arrayBuffer.byteLength} bytes)`);
          }
          // Detectar MIME real desde magic bytes (más fiable que el header en Safari)
          const detectedType = _detectAudioMime(arrayBuffer) || response.headers.get('content-type') || 'audio/mpeg';
          return new Blob([arrayBuffer], { type: detectedType });
        } catch (error) {
          clearTimeout(timeoutId);
          
          if (attempt < 2 && (error.message?.includes('disturbed') || error.name === 'AbortError')) {
            console.warn(`⚠️ Reintentando descarga de ${peticion.audio_file_id}...`);
            await new Promise(r => setTimeout(r, 500));
            return downloadWithRetry(attempt + 1);
          }
          throw error;
        }
      };
      
      const blob = await downloadWithRetry();
      
      // Guardar en caché
      await saveAudioToCache(
        peticion.audio_file_id,
        blob,
        eventId,
        {
          title: peticion.title || peticion.titulo,
          artist: peticion.artist || peticion.artista,
          nota: peticion.nota
        }
      );
      
      completed++;
      logger.log(`✅ ${completed}/${total} - ${peticion.title || peticion.titulo}`);
      
    } catch (error) {
      console.error(`❌ Error descargando ${peticion.audio_file_id}:`, error);
      errors++;
      completed++;
      // RCA feb-2026: propagar el motivo al progress callback para que
      // EventOfflineControls muestre un toast descriptivo (403/401/404 vs red)
      // en vez del genérico "N con error". Guardamos el status/mensaje en un
      // campo compartido en el objeto de progreso.
      if (onProgress) {
        onProgress({
          completed, total, current: peticion, errors,
          lastError: error?.message || String(error),
        });
      }
      continue;
    }
  }
  
  logger.log(`✅ Descarga completada: ${completed - errors}/${total} exitosos, ${errors} errores`);
  return { completed: completed - errors, total, errors };
};

export default {
  saveAudioToCache,
  getAudioFromCache,
  isAudioCached,
  getEventAudios,
  deleteAudioFromCache,
  deleteEventAudios,
  getCacheStats,
  clearAllCache,
  downloadEventAudios
};
